import styles from "./ProductDetails.module.css";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs, addDoc, query, where, writeBatch } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { format2 } from "./utils/format2";
// import { useTheme } from "./ThemeContext"; // Removed unused import

// Standardized units for dropdown selection organized by category
const STANDARD_UNITS = [
  "kg", "g", "lbs", "oz", "units", "pieces", "boxes", "packets", 
  "liters", "ml", "cups", "tbsp", "tsp", "bottles", "cans", "bags"
];

// Unit conversion system - all conversions to base units (g for weight, ml for volume)
const UNIT_CONVERSIONS = {
  // Weight conversions to grams
  'g': 1,
  'kg': 1000,
  'lbs': 453.592,
  'oz': 28.3495,
  
  // Volume conversions to milliliters  
  'ml': 1,
  'liters': 1000,
  'cups': 236.588,
  'tbsp': 14.7868,
  'tsp': 4.92892,
  
  // Count-based units (no conversion needed)
  'units': 1,
  'pieces': 1,
  'boxes': 1,
  'packets': 1,
  'bottles': 1,
  'cans': 1,
  'bags': 1
};

// Unit categories for conversion compatibility
const UNIT_CATEGORIES = {
  weight: ['g', 'kg', 'lbs', 'oz'],
  volume: ['ml', 'liters', 'cups', 'tbsp', 'tsp'],
  count: ['units', 'pieces', 'boxes', 'packets', 'bottles', 'cans', 'bags']
};

// Function to get the category of a unit
const getUnitCategory = (unit) => {
  for (const [category, units] of Object.entries(UNIT_CATEGORIES)) {
    if (units.includes(unit)) return category;
  }
  return null;
};

// Function to convert between units
const convertUnits = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;
  
  const fromCategory = getUnitCategory(fromUnit);
  const toCategory = getUnitCategory(toUnit);
  
  // Can only convert within the same category
  if (!fromCategory || !toCategory || fromCategory !== toCategory) {
    console.warn(`Cannot convert from ${fromUnit} to ${toUnit} - different categories or unknown units`);
    return value; // Return original value if conversion not possible
  }
  
  // Convert from source unit to base unit, then to target unit
  const baseValue = value * UNIT_CONVERSIONS[fromUnit];
  const convertedValue = baseValue / UNIT_CONVERSIONS[toUnit];
  
  return convertedValue;
};

// Function to automatically convert to a better unit for display
const getBestUnit = (value, unit) => {
  const category = getUnitCategory(unit);
  if (!category) return { value, unit };
  
  // Convert to base unit first
  const baseValue = value * UNIT_CONVERSIONS[unit];
  
  // Define preferred units in order of preference for each category
  const preferredUnits = {
    weight: [
      { unit: 'kg', threshold: 1000 },    // Use kg for >= 1000g
      { unit: 'lbs', threshold: 453.592 }, // Use lbs for >= 453.592g (1 lb)
      { unit: 'g', threshold: 0 }         // Use g for everything else
    ],
    volume: [
      { unit: 'liters', threshold: 1000 }, // Use liters for >= 1000ml
      { unit: 'cups', threshold: 236.588 }, // Use cups for >= 236.588ml
      { unit: 'ml', threshold: 0 }         // Use ml for everything else
    ],
    count: [
      { unit: unit, threshold: 0 }        // Keep count units as-is
    ]
  };
  
  const units = preferredUnits[category] || [{ unit: unit, threshold: 0 }];
  
  // Find the best unit (largest unit where value >= threshold)
  for (const { unit: testUnit, threshold } of units) {
    if (baseValue >= threshold) {
      const convertedValue = baseValue / UNIT_CONVERSIONS[testUnit];
      return {
        value: Math.round(convertedValue * 100) / 100, // Round to 2 decimal places
        unit: testUnit
      };
    }
  }
  
  // Fallback to original
  return { value: Math.round(value * 100) / 100, unit };
};

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};


export default function ProductDetails() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const db = getFirestore();
  // const { isDarkMode, toggleTheme } = useTheme(); // Removed unused variables
  const [product, setProduct] = useState(null);
  const [packetsNeeded, setPacketsNeeded] = useState({});
  const [loading, setLoading] = useState(true);
  const [highlightedRecipe, setHighlightedRecipe] = useState(null);

  // Modals
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [editingRecipeIndex, setEditingRecipeIndex] = useState(null);
  const [recipeForm, setRecipeForm] = useState({ recipeName: "", basePackets: 1, isFinal: false });

  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState({ recipeIndex: null, ingIndex: null });
  const [ingredientForm, setIngredientForm] = useState({ 
    name: "", 
    quantity: 0, 
    unit: "", 
    stockUnit: "",
    currentStock: 0, 
    minStock: 5 
  });

  // New: For ingredient and store selection/creation
  const [allIngredients, setAllIngredients] = useState([]);
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [ingredientExists, setIngredientExists] = useState(true);
  // ...existing code...
  const [allStores, setAllStores] = useState([]);
  const [storeSearch, setStoreSearch] = useState("");
  const [selectedStore, setSelectedStore] = useState("");

  // Production tracking for stock updates
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [productionForm, setProductionForm] = useState({
    recipeIndex: 0,
    quantityProduced: 1
  });

  // Final recipe state
  const [showOtherRecipes, setShowOtherRecipes] = useState(false);

  // Mobile menu state
  const [openMobileMenu, setOpenMobileMenu] = useState(null);
  const [openIngredientMenu, setOpenIngredientMenu] = useState(null);

  // Product rename state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newProductName, setNewProductName] = useState("");

  // Fetch all ingredients and stores for search/select
  useEffect(() => {
    const fetchAll = async () => {
      const ingSnap = await getDocs(collection(db, "ingredients"));
      setAllIngredients(ingSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      const storeSnap = await getDocs(collection(db, "stores"));
      setAllStores(storeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchAll();
  }, [db, showIngredientModal]);

  // Watch ingredient name to determine if it exists
  useEffect(() => {
    if (!ingredientForm.name) {
      setIngredientExists(true);
      return;
    }
    const exists = allIngredients.some(
      ing => ing.name.toLowerCase() === ingredientForm.name.toLowerCase()
    );
    setIngredientExists(exists);
  }, [ingredientForm.name, allIngredients]);

  // Fetch product from Firestore
  const fetchProduct = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const docRef = doc(db, "products", productId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const recipes = data.recipes || [];
        const initPackets = {};
        recipes.forEach((r, i) => {
          initPackets[i] = r.basePackets || 1;
        });
        setProduct({ ...data, recipes });
        setPacketsNeeded(initPackets);
      }
    } catch (err) {
      console.error("Error fetching product:", err);
    } finally {
      setLoading(false);
    }
  }, [db, productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]); // Add fetchProduct to dependencies

  // Handle highlighting from spotlight search navigation
  useEffect(() => {
    if (location.state?.highlightRecipe !== undefined) {
      setHighlightedRecipe(location.state.highlightRecipe);
      
      // Clear highlight after 3 seconds
      const timer = setTimeout(() => {
        setHighlightedRecipe(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMobileMenu !== null && !event.target.closest(`.${styles.mobileMenuContainer}`)) {
        setOpenMobileMenu(null);
      }
      if (openIngredientMenu !== null && !event.target.closest(`.${styles.ingredientMenuContainer}`)) {
        setOpenIngredientMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMobileMenu, openIngredientMenu]);

  if (loading || !product) return <p>Loading product...</p>;

  const recipes = product.recipes || [];

  // Delete product handler
  const handleDeleteProduct = async () => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await deleteDoc(doc(db, "products", productId));
      navigate("/products"); // Navigate back to products dashboard
    } catch (err) {
      alert("Failed to delete product.");
      console.error("Error deleting product:", err);
    }
  };

  // Rename product handler
  const handleRenameProduct = async () => {
    if (!newProductName.trim()) {
      alert("Please enter a valid product name.");
      return;
    }
    try {
      await setDoc(doc(db, "products", productId), { 
        ...product, 
        name: newProductName.trim() 
      });
      setProduct({ ...product, name: newProductName.trim() });
      setShowRenameModal(false);
      setNewProductName("");
    } catch (err) {
      alert("Failed to rename product.");
      console.error("Error renaming product:", err);
    }
  };

  // Handle production completion and stock updates with unit conversion
  const handleProductionComplete = async () => {
    if (!product?.recipes?.[productionForm.recipeIndex] || productionForm.quantityProduced <= 0) return;

    try {
      const recipe = product.recipes[productionForm.recipeIndex];
      const multiplier = productionForm.quantityProduced / recipe.basePackets;
      let updateResults = [];
      let hasPermissionError = false;

      // Pre-check: ensure ingredients exist and stock is sufficient
      const consumptionPlans = [];
      let hasBlockingError = false;

      // Update each ingredient's stock with unit conversion
      for (const ingredient of recipe.ingredients) {
        const matchingIngredient = allIngredients.find(ing => 
          ing.name.toLowerCase() === ingredient.name.toLowerCase()
        );
        
        if (matchingIngredient) {
          const usedQuantityInRecipeUnit = ingredient.quantity * multiplier;
          const stockUnit = matchingIngredient.unit || 'units';
          const recipeUnit = ingredient.unit || 'units';
          
          // Convert the used quantity from recipe unit to stock unit
          const usedQuantityInStockUnit = convertUnits(
            usedQuantityInRecipeUnit, 
            recipeUnit, 
            stockUnit
          );
          
          const currentStock = matchingIngredient.currentStock || 0;
          const required = Math.round(usedQuantityInStockUnit * 1000) / 1000;
          const newStock = Math.round((currentStock - required) * 10) / 10;

          if (currentStock < required) {
            hasBlockingError = true;
          }

          consumptionPlans.push({
            ingredientId: matchingIngredient.id,
            ingredientName: matchingIngredient.name,
            stockUnit,
            recipeUnit,
            required,
            currentStock,
            usedQuantityInRecipeUnit
          });
          
          // Track the update for user feedback
          updateResults.push({
            name: ingredient.name,
            usedQuantityRecipe: `${Math.round(usedQuantityInRecipeUnit * 10) / 10} ${recipeUnit}`,
            usedQuantityStock: `${Math.round(usedQuantityInStockUnit * 10) / 10} ${stockUnit}`,
            oldStock: Math.round(currentStock * 10) / 10,
            newStock: Math.max(0, newStock),
            stockUnit: stockUnit
          });
        } else {
          // Ingredient not found in stock
          hasBlockingError = true;
          updateResults.push({
            name: ingredient.name,
            error: 'Not found in stock database'
          });
        }
      }

      // If any ingredient is missing or short, stop before writing anything.
      if (hasBlockingError) {
        let message = `Cannot complete production. Fix inventory first.\n\n`;
        updateResults.forEach(result => {
          if (result.error) {
            message += `• ${result.name}: ${result.error}\n`;
          } else if ((result.oldStock ?? 0) < (Number(String(result.usedQuantityStock).split(' ')[0]) || 0)) {
            message += `• ${result.name}: insufficient stock\n`;
          } else {
            message += `• ${result.name}: OK\n`;
          }
        });
        alert(message);
        return;
      }

      const eventDate = new Date();
      const yearMonth = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, "0")}`;
      const monthShort = eventDate.toLocaleString("en-US", { month: "short" });

      // Sequence number per product per month based on timestamp ordering
      // (avoids composite index by filtering in memory; stable for same-timestamp ties)
      let seq = 1;
      try {
        const seqSnap = await getDocs(query(
          collection(db, "productionEvents"),
          where("productId", "==", productId)
        ));

        const newDateMs = eventDate.getTime();
        const newCreatedMs = newDateMs;

        const isSameMonth = (dateObj) => {
          if (!dateObj) return false;
          const ym = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
          return ym === yearMonth;
        };

        const existing = seqSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .map(evt => {
            const dateObj = toJsDate(evt?.date);
            const createdObj = toJsDate(evt?.createdAt);
            return {
              id: evt.id,
              yearMonth: evt?.yearMonth || (dateObj ? `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}` : null),
              dateMs: dateObj ? dateObj.getTime() : null,
              createdMs: createdObj ? createdObj.getTime() : null
            };
          })
          .filter(evt => {
            if (evt.yearMonth) return evt.yearMonth === yearMonth;
            return isSameMonth(evt.dateMs ? new Date(evt.dateMs) : null);
          });

        // Count how many events sort strictly before this one
        const comesBeforeNew = (evt) => {
          const aDate = (evt.dateMs ?? 0);
          const bDate = newDateMs;
          if (aDate !== bDate) return aDate < bDate;

          const aCreated = (evt.createdMs ?? aDate);
          const bCreated = newCreatedMs;
          if (aCreated !== bCreated) return aCreated < bCreated;

          // Final stable tie-breaker
          return String(evt.id) < "~new";
        };

        seq = existing.filter(comesBeforeNew).length + 1;
      } catch (seqErr) {
        console.error("Error computing batch sequence:", seqErr);
      }

      const batchCode = `${product.name}-${monthShort}-${seq}`;

      // Build a single batched write for: production event, ingredient stock updates, lot consumption, and usage rows.
      const batch = writeBatch(db);
      const productionEventRef = doc(collection(db, 'productionEvents'));

      const productionIngredients = recipe.ingredients.map(ingredient => {
        const matchingIngredient = allIngredients.find(ing =>
          ing.name.toLowerCase() === ingredient.name.toLowerCase()
        );
        const usedQuantityInRecipeUnit = ingredient.quantity * multiplier;
        const stockUnit = matchingIngredient?.unit || 'units';
        const recipeUnit = ingredient.unit || 'units';
        const usedQuantityInStockUnit = convertUnits(usedQuantityInRecipeUnit, recipeUnit, stockUnit);

        return {
          ingredientId: matchingIngredient?.id || null,
          name: ingredient.name,
          quantity: usedQuantityInRecipeUnit,
          unit: recipeUnit,
          quantityStockUnit: usedQuantityInStockUnit,
          stockUnit
        };
      });

      batch.set(productionEventRef, {
        productId,
        productName: product.name,
        recipeName: recipe.recipeName,
        recipeIndex: productionForm.recipeIndex,
        quantityProduced: productionForm.quantityProduced,
        date: eventDate,
        yearMonth,
        batchCode,
        ingredients: productionIngredients,
        createdAt: eventDate,
        fifoMigrated: true
      });

      for (const plan of consumptionPlans) {
        let allocSeq = 0;
        const ingredientRef = doc(db, 'ingredients', plan.ingredientId);
        const newStock = Math.max(0, Math.round((plan.currentStock - plan.required) * 10) / 10);
        batch.update(ingredientRef, {
          currentStock: newStock,
          lastUpdated: eventDate
        });

        // Load lots for FIFO consumption
        // Load lots for FIFO consumption (avoid composite index by sorting in memory)
        const lotsSnap = await getDocs(query(
          collection(db, 'ingredient_inventory_lots'),
          where('ingredientId', '==', plan.ingredientId)
        ));

        const lots = lotsSnap.docs
          .map(d => ({
            id: d.id,
            ref: d.ref,
            ...d.data(),
            remainingQuantity: Number(d.data()?.remainingQuantity || 0)
          }))
          .sort((a, b) => {
            const da = a?.purchaseDate?.seconds ? a.purchaseDate.seconds : (a?.purchaseDate instanceof Date ? Math.floor(a.purchaseDate.getTime() / 1000) : 0);
            const dbb = b?.purchaseDate?.seconds ? b.purchaseDate.seconds : (b?.purchaseDate instanceof Date ? Math.floor(b.purchaseDate.getTime() / 1000) : 0);
            return da - dbb;
          });

        const totalRemaining = lots.reduce((sum, l) => sum + (Number(l.remainingQuantity) || 0), 0);
        const missingBalance = Math.max(0, Math.round((plan.currentStock - totalRemaining) * 1000) / 1000);

        // If stock exists but lots don't fully cover it, top-up an existing legacy opening-balance lot
        // (or create one if none exists). This avoids repeated epoch lots that look like duplicates.
        if (missingBalance > 0) {
          const isEpoch = (pd) => {
            if (!pd) return false;
            if (pd instanceof Date) return pd.getTime() === 0;
            if (typeof pd === "object" && typeof pd.seconds === "number") return pd.seconds === 0;
            return false;
          };

          const existingLegacy = lots.find(l => l?.isLegacy && isEpoch(l?.purchaseDate) && !l?.purchaseHistoryId);
          if (existingLegacy?.ref) {
            const prevPurchased = Number(existingLegacy.quantityPurchased ?? existingLegacy.remainingQuantity ?? 0) || 0;
            const prevRemaining = Number(existingLegacy.remainingQuantity || 0) || 0;
            const nextPurchased = Math.round((prevPurchased + missingBalance) * 1000) / 1000;
            const nextRemaining = Math.round((prevRemaining + missingBalance) * 1000) / 1000;

            existingLegacy.quantityPurchased = nextPurchased;
            existingLegacy.remainingQuantity = nextRemaining;
            batch.update(existingLegacy.ref, {
              quantityPurchased: nextPurchased,
              remainingQuantity: nextRemaining,
              updatedAt: eventDate
            });
          } else {
            const legacyLotRef = doc(collection(db, 'ingredient_inventory_lots'));
            batch.set(legacyLotRef, {
              ingredientId: plan.ingredientId,
              ingredientName: plan.ingredientName,
              unit: plan.stockUnit,
              purchaseDate: new Date(0),
              quantityPurchased: missingBalance,
              remainingQuantity: missingBalance,
              isLegacy: true,
              createdAt: eventDate,
              updatedAt: eventDate
            });
            lots.unshift({
              id: legacyLotRef.id,
              ref: legacyLotRef,
              ingredientId: plan.ingredientId,
              ingredientName: plan.ingredientName,
              unit: plan.stockUnit,
              purchaseDate: new Date(0),
              quantityPurchased: missingBalance,
              remainingQuantity: missingBalance,
              isLegacy: true
            });
          }
        }

        // If there are multiple legacy epoch lots, merge their remaining into one to avoid
        // splitting a single production across many legacy lots (which looks like duplicates).
        {
          const isEpoch = (pd) => {
            if (!pd) return false;
            if (pd instanceof Date) return pd.getTime() === 0;
            if (typeof pd === "object" && typeof pd.seconds === "number") return pd.seconds === 0;
            return false;
          };

          const legacyLots = lots.filter(l => l?.isLegacy && isEpoch(l?.purchaseDate) && !l?.purchaseHistoryId);
          if (legacyLots.length > 1) {
            // Pick a stable primary lot (first in list, which is earliest due to epoch date).
            const primary = legacyLots[0];
            let mergedRemaining = Number(primary.remainingQuantity || 0) || 0;

            for (const extra of legacyLots.slice(1)) {
              const extraRemaining = Number(extra.remainingQuantity || 0) || 0;
              if (extraRemaining > 0 && extra?.ref) {
                mergedRemaining = Math.round((mergedRemaining + extraRemaining) * 1000) / 1000;
                extra.remainingQuantity = 0;
                batch.update(extra.ref, { remainingQuantity: 0, updatedAt: eventDate });
              }
            }

            if (primary?.ref) {
              primary.remainingQuantity = mergedRemaining;
              batch.update(primary.ref, { remainingQuantity: mergedRemaining, updatedAt: eventDate });
            }
          }
        }

        let remainingToConsume = plan.required;
        for (const lot of lots) {
          if (remainingToConsume <= 0) break;
          const lotRemaining = Number(lot.remainingQuantity || 0);
          if (lotRemaining <= 0) continue;

          const usedFromLot = Math.min(lotRemaining, remainingToConsume);
          const newLotRemaining = Math.round((lotRemaining - usedFromLot) * 1000) / 1000;

          batch.update(lot.ref, {
            remainingQuantity: newLotRemaining,
            updatedAt: eventDate
          });

          const usageRef = doc(
            db,
            'ingredient_usage_history',
            `${productionEventRef.id}-${plan.ingredientId}-${lot.id}-${allocSeq}`
          );
          allocSeq += 1;
          batch.set(usageRef, {
            ingredientId: plan.ingredientId,
            ingredientName: plan.ingredientName,
            unit: plan.stockUnit,
            quantityUsed: usedFromLot,
            date: eventDate,
            productId,
            productName: product.name,
            recipeName: recipe.recipeName,
            quantityProduced: productionForm.quantityProduced,
            productionEventId: productionEventRef.id,
            batchCode,
            yearMonth,
            lotId: lot.id,
            lotPurchaseDate: lot.purchaseDate || null,
            purchaseHistoryId: lot.purchaseHistoryId || null,
            lotRemainingAfter: newLotRemaining,
            createdAt: eventDate
          });

          remainingToConsume = Math.round((remainingToConsume - usedFromLot) * 1000) / 1000;
        }

        if (remainingToConsume > 0) {
          // This indicates lots are out-of-sync with ingredient stock.
          console.warn(`FIFO allocation shortfall for ${plan.ingredientName}: missing ${remainingToConsume} ${plan.stockUnit}`);
        }
      }

      try {
        await batch.commit();
      } catch (commitError) {
        console.error('Error committing production batch:', commitError);
        if (commitError.code === 'permission-denied') {
          hasPermissionError = true;
        } else {
          throw commitError;
        }
      }

      // Track production event in Firestore (only if no permission errors)
      // (Production event tracking is now included in the batched commit above.)

      // Refresh ingredients data
      try {
        const ingSnap = await getDocs(collection(db, "ingredients"));
        setAllIngredients(ingSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (refreshError) {
        console.error("Error refreshing ingredients:", refreshError);
      }

      setShowProductionModal(false);
      setProductionForm({ recipeIndex: 0, quantityProduced: 1 });
      
      // Show detailed update results
      if (hasPermissionError) {
        alert(`Permission Error: You don't have permission to update inventory.\n\nPlease check:\n1. You are logged in as a user (not 'new' role)\n2. Firebase security rules allow updates\n3. Contact admin if needed`);
      } else {
        let message = `Production completed!\nBatch: ${batchCode}\n\nStock Updates:\n`;
        updateResults.forEach(result => {
          if (result.error) {
            message += `• ${result.name}: ${result.error}\n`;
          } else {
            message += `• ${result.name}: ${result.oldStock} → ${result.newStock} ${result.stockUnit}\n`;
            if (result.usedQuantityRecipe !== result.usedQuantityStock) {
              message += `  (Used: ${result.usedQuantityRecipe} = ${result.usedQuantityStock})\n`;
            }
          }
        });
        
        alert(message);
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      if (error.code === 'permission-denied') {
        alert("Permission Error: You don't have permission to update inventory. Please contact your administrator.");
      } else {
        alert(`Error updating stock: ${error.message}`);
      }
    }
  };

  // === Recipe Modal Handlers ===
  const handleRecipeFormSubmit = async () => {
    const updatedRecipes = [...recipes];
    
    // If setting this recipe as final, reset all others to not final
    if (recipeForm.isFinal) {
      updatedRecipes.forEach(recipe => {
        recipe.isFinal = false;
      });
    }
    
    if (editingRecipeIndex !== null) {
      // Update existing recipe
      updatedRecipes[editingRecipeIndex] = { 
        ...updatedRecipes[editingRecipeIndex], 
        recipeName: recipeForm.recipeName,
        basePackets: recipeForm.basePackets,
        isFinal: recipeForm.isFinal
      };
    } else {
      // Add new recipe
      updatedRecipes.push({ 
        recipeName: recipeForm.recipeName,
        basePackets: recipeForm.basePackets,
        isFinal: recipeForm.isFinal,
        ingredients: [] 
      });
    }
    
    await setDoc(doc(db, "products", productId), { ...product, recipes: updatedRecipes });
    await fetchProduct();
    setShowRecipeModal(false);
    setEditingRecipeIndex(null);
    setRecipeForm({ recipeName: "", basePackets: 1, isFinal: false });
    
    // Hide other recipes if we just set one as final
    if (recipeForm.isFinal) {
      setShowOtherRecipes(false);
    }
  };

  const deleteRecipe = async (index) => {
    if (!window.confirm("Delete this recipe?")) return;
    const updatedRecipes = recipes.filter((_, i) => i !== index);
    await setDoc(doc(db, "products", productId), { ...product, recipes: updatedRecipes });
    await fetchProduct();
    const newPackets = { ...packetsNeeded };
    delete newPackets[index];
    setPacketsNeeded(newPackets);
  };

  // === Ingredient Modal Handlers (updated) ===
  const handleIngredientFormSubmit = async (e) => {
    if (e) e.preventDefault();
    const updatedRecipes = [...recipes];
    const { recipeIndex, ingIndex } = editingIngredient;

    let ingredientId = "";
    let storeId = "";

    // If ingredient exists, get its id and storeId
    const existingIng = allIngredients.find(
      ing => ing.name.toLowerCase() === ingredientForm.name.toLowerCase()
    );
    if (existingIng) {
      ingredientId = existingIng.id;
      storeId = existingIng.storeId;
    } else {
      // Create store if needed
      let storeDocId = "";
      const existingStore = allStores.find(
        s => s.name.toLowerCase() === selectedStore.toLowerCase()
      );
      if (existingStore) {
        storeDocId = existingStore.id;
      } else {
        const storeDocRef = await addDoc(collection(db, "stores"), { name: selectedStore });
        storeDocId = storeDocRef.id;
        setAllStores([...allStores, { id: storeDocId, name: selectedStore }]);
      }
      // Create ingredient
      const ingDocRef = await addDoc(collection(db, "ingredients"), {
        name: ingredientForm.name,
        storeId: storeDocId,
        currentStock: Number(ingredientForm.currentStock) || 0,
        minStock: Number(ingredientForm.minStock) || 5,
        unit: ingredientForm.stockUnit,
        createdAt: new Date(),
        lastUpdated: new Date(),
      });
      ingredientId = ingDocRef.id;
      storeId = storeDocId;
      setAllIngredients([...allIngredients, { 
        id: ingredientId, 
        name: ingredientForm.name, 
        storeId,
        currentStock: Number(ingredientForm.currentStock) || 0,
        minStock: Number(ingredientForm.minStock) || 5,
        unit: ingredientForm.stockUnit
      }]);
    }

    const newIngredient = {
      name: ingredientForm.name,
      quantity: ingredientForm.quantity,
      unit: ingredientForm.unit,
      ingredientId,
      storeId,
    };

    if (ingIndex !== null) {
      updatedRecipes[recipeIndex].ingredients[ingIndex] = newIngredient;
    } else {
      if (!updatedRecipes[recipeIndex].ingredients) updatedRecipes[recipeIndex].ingredients = [];
      updatedRecipes[recipeIndex].ingredients.push(newIngredient);
    }

    await setDoc(doc(db, "products", productId), { ...product, recipes: updatedRecipes });
    await fetchProduct();
    setShowIngredientModal(false);
    setEditingIngredient({ recipeIndex: null, ingIndex: null });
    setIngredientForm({ name: "", quantity: 0, unit: "", stockUnit: "", currentStock: 0, minStock: 5 });
    setIngredientSearch("");
    setStoreSearch("");
    setSelectedStore("");
  };

  const deleteIngredient = async (recipeIndex, ingIndex) => {
    if (!window.confirm("Delete this ingredient?")) return;
    const updatedRecipes = [...recipes];
    updatedRecipes[recipeIndex].ingredients.splice(ingIndex, 1);
    await setDoc(doc(db, "products", productId), { ...product, recipes: updatedRecipes });
    await fetchProduct();
  };

  const cloneIngredient = (recipeIndex, ingIndex) => {
    const ingredient = recipes[recipeIndex].ingredients[ingIndex];
    
    // Find the store information for this ingredient
    const stockIngredient = allIngredients.find(ing => 
      ing.name.toLowerCase() === ingredient.name.toLowerCase()
    );
    const storeInfo = stockIngredient ? allStores.find(store => store.id === stockIngredient.storeId) : null;
    
    setEditingIngredient({ recipeIndex, ingIndex: null });
    setIngredientForm({
      name: `${ingredient.name} (Copy)`,
      quantity: ingredient.quantity,
      unit: ingredient.unit,
      stockUnit: stockIngredient?.unit || ingredient.unit || "",
      currentStock: 0,
      minStock: stockIngredient?.minStock || 5
    });
    
    // Set store information if available
    if (storeInfo) {
      setSelectedStore(storeInfo.name);
      setStoreSearch(storeInfo.name);
    } else {
      setSelectedStore("");
      setStoreSearch("");
    }
    
    setShowIngredientModal(true);
  };

  // Get final recipe and other recipes
  const finalRecipe = recipes.find(recipe => recipe.isFinal);
  // ...existing code...
  const otherRecipes = recipes.filter(recipe => !recipe.isFinal);
  const recipesToShow = finalRecipe ? (showOtherRecipes ? recipes : [finalRecipe]) : recipes;

  return (
    <div className={styles.dashboardContainer}>
      {/* Back button */}
      <button
        onClick={() => navigate("/products")}
        className={`${styles.navButton} ${styles.backButton}`}
      >
        ← Back to Products
      </button>
      
      {/* Removed theme toggle button */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{product.name}</h1>
      </div>
      <div className={styles.productButtons}>
        <button
          onClick={() => {
            setNewProductName(product.name);
            setShowRenameModal(true);
          }}
          className={styles.renameButton}
        >
          ✏️
        </button>
        <button
          onClick={handleDeleteProduct}
          className={styles.deleteProduct}
        >
          Delete Product
        </button>
      <button
        onClick={() => {
          setEditingRecipeIndex(null);
          setRecipeForm({ recipeName: "", basePackets: 1, isFinal: false });
          setShowRecipeModal(true);
        }}
        className={styles.addRecipe}
      >
        + Add Recipe
      </button>
      </div>

      {recipes.length === 0 && <p className="text-gray-500">No recipes yet.</p>}

      {/* Final Recipe Status and Toggle */}
      {finalRecipe && otherRecipes.length > 0 && (
        <div className={styles.finalRecipeStatus}>
          <div className={styles.finalRecipeStatusContent}>
            <div className={styles.finalRecipeInfo}>
              <span className={styles.finalRecipePin}>📌</span>
              <span className={styles.finalRecipeTitle}>Final Recipe: </span>
              <span className={styles.finalRecipeName}>{finalRecipe.recipeName}</span>
              <span className={styles.finalRecipeCount}>
                ({otherRecipes.length} other recipe{otherRecipes.length > 1 ? 's' : ''} hidden)
              </span>
            </div>
            <button
              onClick={() => setShowOtherRecipes(!showOtherRecipes)}
              className={styles.finalRecipeToggle}
            >
              {showOtherRecipes ? 'Hide Other Recipes' : 'Show All Recipes'}
            </button>
          </div>
        </div>
      )}

      {/* Recipes Card Grid */}
      <div className={styles.cardGrid}>
        {recipesToShow.map((recipe, displayIndex) => {
          // Get the actual index in the original recipes array
          const actualIndex = recipes.findIndex(r => r === recipe);
          const currentPackets = packetsNeeded[actualIndex] || recipe.basePackets;
          const scaledIngredients = recipe.ingredients.map((ing) => {
            const scaledQuantity = (ing.quantity / recipe.basePackets) * currentPackets;
            const bestUnit = getBestUnit(scaledQuantity, ing.unit);
            return {
              ...ing,
              scaledQuantity: scaledQuantity,
              displayQuantity: bestUnit.value,
              displayUnit: bestUnit.unit
            };
          });

          // Check if recipe has ingredients and if they're available in stock
          const hasIngredients = recipe.ingredients && recipe.ingredients.length > 0;
          const canProduce = hasIngredients && recipe.ingredients.every(ingredient => {
            const stockIngredient = allIngredients.find(stockIng => 
              stockIng.name.toLowerCase() === ingredient.name.toLowerCase()
            );
            return stockIngredient && stockIngredient.currentStock > 0;
          });

          return (
            <div key={actualIndex} className={`${styles.recipeCard} ${
              recipe.isFinal ? styles.finalRecipeCard : ''
            } ${highlightedRecipe === actualIndex ? styles.highlighted : ''}`}>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold">{recipe.recipeName}</h2>
                </div>
                
                {/* Desktop Button Group */}
                <div className={styles.desktopButtonGroup}>
                  <button
                    className={`${styles.productionButton} ${
                      canProduce 
                        ? styles.productionButtonEnabled 
                        : styles.productionButtonDisabled
                    }`}
                    onClick={() => {
                      if (canProduce) {
                        setProductionForm({ 
                          recipeIndex: actualIndex, 
                          quantityProduced: currentPackets 
                        });
                        setShowProductionModal(true);
                      }
                    }}
                    disabled={!canProduce}
                    title={!canProduce ? 'No ingredients available or recipe has no ingredients' : 'Record Production'}
                  >
                    Record Production
                  </button>
                  <button
                    className={styles.recipeEdit}
                    onClick={() => {
                      setEditingRecipeIndex(actualIndex);
                      setRecipeForm({ 
                        recipeName: recipe.recipeName, 
                        basePackets: recipe.basePackets,
                        isFinal: recipe.isFinal || false
                      });
                      setShowRecipeModal(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteRecipe(actualIndex)}
                    className={styles.recipeDelete}
                  >
                    Delete
                  </button>
                  <input
                    type="number"
                    value={currentPackets}
                    onChange={(e) =>
                      setPacketsNeeded({ ...packetsNeeded, [actualIndex]: Number(e.target.value) })
                    }
                    className={styles.recipeCount}
                  />
                </div>

                {/* Mobile 3-dot Menu */}
                <div className={styles.mobileMenuContainer}>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={currentPackets}
                      onChange={(e) =>
                        setPacketsNeeded({ ...packetsNeeded, [actualIndex]: Number(e.target.value) })
                      }
                      className={styles.recipeCount}
                    />
                    <button
                      className={styles.mobileMenuButton}
                      onClick={() => setOpenMobileMenu(openMobileMenu === actualIndex ? null : actualIndex)}
                    >
                      ⋮
                    </button>
                  </div>
                  
                  {openMobileMenu === actualIndex && (
                    <div className={styles.mobileDropdown}>
                      <button
                        className={`${styles.mobileDropdownItem} ${
                          !canProduce ? styles.mobileDropdownItemDisabled : ''
                        }`}
                        onClick={() => {
                          if (canProduce) {
                            setProductionForm({ 
                              recipeIndex: actualIndex, 
                              quantityProduced: currentPackets 
                            });
                            setShowProductionModal(true);
                            setOpenMobileMenu(null);
                          }
                        }}
                        disabled={!canProduce}
                        title={!canProduce ? 'No ingredients available or recipe has no ingredients' : 'Record Production'}
                      >
                        📊 Record Production
                      </button>
                      <div className={styles.mobileDropdownSeparator}></div>
                      <button
                        className={styles.mobileDropdownItem}
                        onClick={() => {
                          setEditingRecipeIndex(actualIndex);
                          setRecipeForm({ 
                            recipeName: recipe.recipeName, 
                            basePackets: recipe.basePackets,
                            isFinal: recipe.isFinal || false
                          });
                          setShowRecipeModal(true);
                          setOpenMobileMenu(null);
                        }}
                      >
                        ✏️ Edit Recipe
                      </button>
                      <button
                        className={styles.mobileDropdownItem}
                        onClick={() => {
                          deleteRecipe(actualIndex);
                          setOpenMobileMenu(null);
                        }}
                      >
                        🗑️ Delete Recipe
                      </button>
                    </div>
                  )}
                </div>
              </div>


                  {recipe.ingredients.length === 0 ? (
                    <p className="text-gray-500 mb-2">No ingredients yet. Add one below.</p>
                  ) : (
                    <table className="table-auto border-collapse border w-full mb-2">
                    <thead>
                      <tr>
                      <th className="border px-2 py-1">Name</th>
                      <th className="border px-2 py-1">Quantity</th>
                      <th className="border px-2 py-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {scaledIngredients.map((ing, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{ing.name}</td>
                        <td className="border px-2 py-1">
                          {format2(ing.displayQuantity)} {ing.displayUnit}
                          {ing.displayUnit !== ing.unit && (
                            <div className={styles.unitConversion}>
                              (originally {format2(ing.scaledQuantity)} {ing.unit})
                            </div>
                          )}
                        </td>
                        <td className="border px-2 py-1">
                          <div className={styles.ingredientMenuContainer}>
                            <button
                              className={styles.ingredientMenuButton}
                              onClick={() => setOpenIngredientMenu(
                                openIngredientMenu === `${actualIndex}-${idx}` ? null : `${actualIndex}-${idx}`
                              )}
                            >
                              ⋮
                            </button>
                            
                            {openIngredientMenu === `${actualIndex}-${idx}` && (
                              <div className={styles.ingredientDropdown}>
                                <button
                                  className={styles.ingredientDropdownItem}
                                  onClick={() => {
                                    setEditingIngredient({ recipeIndex: actualIndex, ingIndex: idx });
                                    setIngredientForm({ ...ing });
                                    setShowIngredientModal(true);
                                    setOpenIngredientMenu(null);
                                  }}
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  className={styles.ingredientDropdownItem}
                                  onClick={() => {
                                    cloneIngredient(actualIndex, idx);
                                    setOpenIngredientMenu(null);
                                  }}
                                >
                                  📋 Clone
                                </button>
                                <button
                                  className={styles.ingredientDropdownItem}
                                  onClick={() => {
                                    deleteIngredient(actualIndex, idx);
                                    setOpenIngredientMenu(null);
                                  }}
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      ))}
                    </tbody>
                    </table>
                  )}

                  <button
                    onClick={() => {
                setEditingIngredient({ recipeIndex: actualIndex, ingIndex: null });
                setIngredientForm({ name: "", quantity: 0, unit: "", stockUnit: "", currentStock: 0, minStock: 5 });
                setShowIngredientModal(true);
              }}
              className={styles.addIngredientButton}
            >
              + Add Ingredient
            </button>
          </div>
        );
      })}

      {/* === Recipe Modal === */}
      {showRecipeModal && (
        <div className={styles.Modal}>
          <div className="modalCard">
            <h2 className="modalTitle">
              {editingRecipeIndex !== null ? "Edit Recipe" : "Add Recipe"}
            </h2>
            <label className="block mb-2">Recipe Name</label>
            <input
              type="text"
              className="border p-1 mb-4 w-full rounded"
              value={recipeForm.recipeName}
              onChange={(e) => setRecipeForm({ ...recipeForm, recipeName: e.target.value })}
            />
            <label className="block mb-2">Base Packets</label>
            <input
              type="number"
              className="border p-1 mb-4 w-full rounded"
              value={recipeForm.basePackets}
              onChange={(e) => setRecipeForm({ ...recipeForm, basePackets: Number(e.target.value) })}
            />
            
            <div className={styles.finalRecipeCheckbox}>
              <input
                type="checkbox"
                checked={recipeForm.isFinal}
                onChange={(e) => setRecipeForm({ ...recipeForm, isFinal: e.target.checked })}
                id="finalRecipeCheckbox"
              />
              <label htmlFor="finalRecipeCheckbox" className={styles.finalRecipeCheckboxLabel}>
                <span className={styles.finalRecipeCheckboxText}>Set as final recipe</span>
                <span className={styles.finalRecipeCheckboxSubtext}>(will hide other recipes by default)</span>
              </label>
            </div>
            
            <div className="flex justify-end gap-2">
              <button onClick={() => {
                setShowRecipeModal(false);
                setRecipeForm({ recipeName: "", basePackets: 1, isFinal: false });
                setEditingRecipeIndex(null);
              }} className="px-3 py-1 rounded border">
                Cancel
              </button>
              <button onClick={handleRecipeFormSubmit} className="px-3 py-1 rounded bg-green-500 text-white">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === Ingredient Modal === */}
      {showIngredientModal && (
        <div className={styles.Modal}>
          <div className="modalCard">
            <h2 className="modalTitle">{editingIngredient.ingIndex !== null ? "Edit" : "Add"} Ingredient</h2>
            <form onSubmit={handleIngredientFormSubmit}>
              <label className="block mb-2">Ingredient Name</label>
              <input
                type="text"
                className="border p-1 mb-2 w-full rounded"
                placeholder="Search or type to add new ingredient"
                value={ingredientForm.name}
                onChange={e => {
                  setIngredientForm({ ...ingredientForm, name: e.target.value });
                  setIngredientSearch(e.target.value);
                }}
                required
                list="ingredient-options"
                autoComplete="off"
              />
              <datalist id="ingredient-options">
                {allIngredients
                  .filter(ing => ing.name.toLowerCase().includes(ingredientSearch.toLowerCase()))
                  .map(ing => (
                    <option key={ing.id} value={ing.name} />
                  ))}
              </datalist>
              {!ingredientExists && (
                <div className="text-xs text-gray-500 mb-2">
                  This will create a new ingredient: <b>{ingredientForm.name}</b>
                </div>
              )}
              {/* Recipe quantity and unit section */}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
                <h4 className="font-medium text-gray-800 mb-3">Recipe Information</h4>
                
                <label className="block mb-2">Quantity needed for this recipe</label>
                <input
                  type="number"
                  className="border p-1 mb-2 w-full rounded"
                  value={ingredientForm.quantity}
                  onChange={e => setIngredientForm({ ...ingredientForm, quantity: Number(e.target.value) })}
                  required
                  min={0}
                  step="0.1"
                />
                
                <label className="block mb-2">Recipe Unit</label>
                <select
                  className="border p-1 mb-2 w-full rounded"
                  value={ingredientForm.unit}
                  onChange={e => setIngredientForm({ ...ingredientForm, unit: e.target.value })}
                  required
                >
                  <option value="">Select a unit...</option>
                  {STANDARD_UNITS.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>

              {/* If ingredient does not exist, show creation section */}
              {!ingredientExists && (
                <div className={styles.newIngredientSetup}>
                  <h4 className="font-medium text-gray-800 mb-3">New Ingredient Setup</h4>
                  
                  <label className="block mb-2">Store</label>
                  <input
                    type="text"
                    className="border p-1 mb-2 w-full rounded"
                    placeholder="Search or type to add new store"
                    value={selectedStore}
                    onChange={e => {
                      setSelectedStore(e.target.value);
                      setStoreSearch(e.target.value);
                    }}
                    required
                    list="store-options"
                    autoComplete="off"
                  />
                  <datalist id="store-options">
                    {allStores
                      .filter(store => store.name.toLowerCase().includes(storeSearch.toLowerCase()))
                      .map(store => (
                        <option key={store.id} value={store.name} />
                      ))}
                  </datalist>
                  {selectedStore && !allStores.some(s => s.name.toLowerCase() === selectedStore.toLowerCase()) && (
                    <div className="text-xs text-gray-500 mb-2">
                      This will create a new store: <b>{selectedStore}</b>
                    </div>
                  )}
                  
                  <label className="block mb-2">Current Stock Amount</label>
                  <input
                    type="number"
                    className="border p-1 mb-2 w-full rounded"
                    placeholder="0"
                    value={ingredientForm.currentStock}
                    onChange={e => setIngredientForm({ 
                      ...ingredientForm, 
                      currentStock: Number(e.target.value) 
                    })}
                    min="0"
                    step="0.1"
                    required
                  />
                  
                  <label className="block mb-2">Stock Unit (how you measure inventory)</label>
                  <select
                    className="border p-1 mb-2 w-full rounded"
                    value={ingredientForm.stockUnit}
                    onChange={e => setIngredientForm({ ...ingredientForm, stockUnit: e.target.value })}
                    required
                  >
                    <option value="">Select a unit...</option>
                    {STANDARD_UNITS.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                  
                  <label className="block mb-2">Minimum Stock Alert</label>
                  <input
                    type="number"
                    className="border p-1 mb-2 w-full rounded"
                    placeholder="5"
                    value={ingredientForm.minStock}
                    onChange={e => setIngredientForm({ 
                      ...ingredientForm, 
                      minStock: Number(e.target.value) 
                    })}
                    min="0"
                    step="0.1"
                    required
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowIngredientModal(false);
                    setEditingIngredient({ recipeIndex: null, ingIndex: null });
                    setIngredientForm({ name: "", quantity: 0, unit: "", stockUnit: "", currentStock: 0, minStock: 5 });
                    setIngredientSearch("");
                    setStoreSearch("");
                    setSelectedStore("");
                  }}
                  className="px-3 py-1 rounded border"
                >
                  Cancel
                </button>
                <button type="submit" className="px-3 py-1 rounded bg-green-500 text-white">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Production Modal */}
      {showProductionModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Record Production</h2>
            </div>
            <div className={styles.modalBody}>
              {product?.recipes?.[productionForm.recipeIndex] && (
                <div>
                  <div className={styles.productionInfo}>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-medium">Recipe:</span> {product.recipes[productionForm.recipeIndex].recipeName}
                    </p>
                    <p className="text-xs text-gray-600">
                      This will automatically deduct ingredients from stock based on your production quantity.
                    </p>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block mb-2 font-medium text-gray-700">Quantity Produced</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className={styles.productionInput}
                      value={productionForm.quantityProduced}
                      onChange={(e) => setProductionForm({
                        ...productionForm,
                        quantityProduced: Number(e.target.value)
                      })}
                      required
                    />
                  </div>
                  
                  <div className={styles.ingredientPreview}>
                    <h4 className="font-medium text-sm mb-3 text-amber-800">Ingredients to be deducted:</h4>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {product.recipes[productionForm.recipeIndex].ingredients.map((ing, idx) => {
                        const multiplier = productionForm.quantityProduced / product.recipes[productionForm.recipeIndex].basePackets;
                        const usedQuantityRecipe = (ing.quantity * multiplier);
                        
                        const stockIngredient = allIngredients.find(stockIng => 
                          stockIng.name.toLowerCase() === ing.name.toLowerCase()
                        );
                        
                        const recipeUnit = ing.unit || 'units';
                        const stockUnit = stockIngredient?.unit || 'units';
                        const currentStock = stockIngredient?.currentStock || 0;
                        
                        const usedQuantityStock = convertUnits(usedQuantityRecipe, recipeUnit, stockUnit);
                        const newStock = Math.max(0, currentStock - usedQuantityStock);
                        
                        // Get better display units
                        const bestRecipeUnit = getBestUnit(usedQuantityRecipe, recipeUnit);
                        const bestStockUnit = getBestUnit(usedQuantityStock, stockUnit);
                        
                        return (
                          <div key={idx} className={styles.ingredientItem}>
                            <div className="font-medium text-gray-800 mb-1">{ing.name}</div>
                            <div className="text-sm text-gray-600 mb-1">
                              Recipe uses: <span className="font-medium">{format2(bestRecipeUnit.value)} {bestRecipeUnit.unit}</span>
                              {bestRecipeUnit.unit !== recipeUnit && (
                                <span className={`block ${styles.unitConversion}`}>
                                  (originally {format2(usedQuantityRecipe)} {recipeUnit})
                                </span>
                              )}
                            </div>
                            {stockIngredient ? (
                              <>
                                {recipeUnit !== stockUnit && (
                                  <div className="text-sm text-blue-600 mb-1">
                                    Converted: <span className="font-medium">{format2(bestStockUnit.value)} {bestStockUnit.unit}</span>
                                    {bestStockUnit.unit !== stockUnit && (
                                      <span className={`block ${styles.unitConversion}`}>
                                        (originally {format2(usedQuantityStock)} {stockUnit})
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div className="text-sm text-gray-600 mb-1">
                                  Stock: <span className="font-medium">{format2(currentStock)} → {format2(newStock)} {stockUnit}</span>
                                </div>
                                {newStock === 0 && currentStock > 0 && (
                                  <div className="text-sm text-orange-600 font-medium">
                                    <span className="mr-1">⚠️</span> Stock will be depleted
                                  </div>
                                )}
                                {usedQuantityStock > currentStock && (
                                  <div className="text-sm text-red-600 font-medium">
                                    <span className="mr-1">❌</span> Insufficient stock!
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-sm text-red-600 font-medium">
                                <span className="mr-1">❌</span> Ingredient not found in stock
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className={styles.modalFooter}>
              <button
                onClick={() => {
                  setShowProductionModal(false);
                  setProductionForm({ recipeIndex: 0, quantityProduced: 1 });
                }}
                className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={handleProductionComplete}
                className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
                disabled={productionForm.quantityProduced <= 0}
              >
                Complete Production
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Product Modal */}
      {showRenameModal && (
        <div className={styles.Modal}>
          <div className="modalCard">
            <h2 className="modalTitle">Rename Product</h2>
            <label className="block mb-2">Product Name</label>
            <input
              type="text"
              className="border p-1 mb-4 w-full rounded"
              value={newProductName}
              onChange={(e) => setNewProductName(e.target.value)}
              placeholder="Enter new product name"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => {
                  setShowRenameModal(false);
                  setNewProductName("");
                }} 
                className="px-3 py-1 rounded border"
              >
                Cancel
              </button>
              <button 
                onClick={handleRenameProduct} 
                className="px-3 py-1 rounded bg-green-500 text-white"
                disabled={!newProductName.trim()}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      </div>
  );
}

